import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'sonner';

import { packagingApi } from 'api/packaging';
import { PackagingPOCreateItemsCard } from 'components/packaging/components/packagingPOV2/create/PackagingPOCreateItemsCard';
import { PackagingPOCreatePageHeader } from 'components/packaging/components/packagingPOV2/create/PackagingPOCreatePageHeader';
import { PackagingPOCreatePrimaryCard } from 'components/packaging/components/packagingPOV2/create/PackagingPOCreatePrimaryCard';
import {
    extractFacilityIds,
    validateCreatePackagingPO,
} from 'components/packaging/components/packagingPOV2/utils/packagingPOValidation';

const DEFAULT_DRAFT = {
    vendorId: '',
    facilities: [],
    scheduledDeliveryDate: '',
    scheduledTime: '',
    referenceNumber: '',
};

export function CreatePackagingPOV2() {
    const history = useHistory();

    const [draft, setDraft] = useState(DEFAULT_DRAFT);
    const [items, setItems] = useState([]);
    const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isSubmittingRef = useRef(false);

    const validation = useMemo(() => validateCreatePackagingPO({ draft, items }), [draft, items]);

    const handleSave = useCallback(async () => {
        if (isSubmittingRef.current) return;
        isSubmittingRef.current = true;

        setHasAttemptedSubmit(true);
        if (!validation.isValid) {
            isSubmittingRef.current = false;
            return;
        }

        setIsSubmitting(true);
        const createdFacilityIds = [];
        const facilityIds = extractFacilityIds(draft.facilities);
        const facilityNamesById = new Map(
            (Array.isArray(draft.facilities) ? draft.facilities : [])
                .map((facility) => {
                    if (facility == null) return null;
                    if (typeof facility === 'object' && facility.id != null) {
                        return [Number(facility.id), facility.name ?? facility.label ?? null];
                    }
                    return [Number(facility), null];
                })
                .filter(Boolean),
        );
        try {
            const baseParams = {
                vendorID: Number(draft.vendorId),
                deliverDate: draft.scheduledDeliveryDate,
                deliverTime: draft.scheduledTime,
                referenceNumber: draft.referenceNumber,
                items: validation.submitItems,
            };

            const results = await Promise.all(
                facilityIds.map(async (facilityId) => {
                    try {
                        const response = await packagingApi.createPurchaseOrder({
                            ...baseParams,
                            facilityID: facilityId,
                        });
                        return { facilityId, response };
                    } catch (error) {
                        return { facilityId, error };
                    }
                }),
            );

            const failures = [];
            results.forEach((result) => {
                if (result.error) {
                    failures.push({
                        facilityId: result.facilityId,
                        message: result.error?.message ?? null,
                    });
                    return;
                }
                if (result.response?.status === false) {
                    failures.push({
                        facilityId: result.facilityId,
                        message: result.response?.msg ?? null,
                    });
                    return;
                }
                createdFacilityIds.push(result.facilityId);
            });

            if (failures.length > 0) {
                if (createdFacilityIds.length > 0) {
                    const createdLabels = createdFacilityIds
                        .map((id) => facilityNamesById.get(id) || `Facility ${id}`)
                        .join(', ');
                    toast.success(`Created purchase orders for: ${createdLabels}`);
                }
                const failureSummary = failures
                    .map(({ facilityId, message }) => {
                        const label = facilityNamesById.get(facilityId) || `Facility ${facilityId}`;
                        return message ? `${label} (${message})` : label;
                    })
                    .join(', ');
                toast.error(`Failed to create purchase orders for: ${failureSummary}`);
                return;
            }

            toast.success('Purchase Order created!');
            history.push('/packaging/viewPurchaseOrder-v2');
        } catch (error) {
            if (createdFacilityIds.length > 0) {
                const createdLabels = createdFacilityIds.map((id) => `Facility ${id}`).join(', ');
                toast.success(`Created purchase orders for: ${createdLabels}`);
            }
            toast.error(error?.message ?? 'Error creating purchase order');
        } finally {
            isSubmittingRef.current = false;
            setIsSubmitting(false);
        }
    }, [draft, history, validation.isValid, validation.submitItems]);

    return (
        <div className="flex min-h-screen flex-col gap-6 px-4 py-4">
            <PackagingPOCreatePageHeader
                title="Create Packaging Purchase Order"
                statusLabel="Creating"
                onBack={() => history.push('/packaging/viewPurchaseOrder-v2')}
                onCancel={() => history.push('/packaging/viewPurchaseOrder-v2')}
                onSave={handleSave}
                isDisabled={isSubmitting}
            />

            <PackagingPOCreatePrimaryCard
                draft={draft}
                onDraftChange={setDraft}
                showValidation={hasAttemptedSubmit}
                primaryErrors={validation.primaryErrors}
                primaryFieldErrors={validation.primaryFieldErrors}
            />

            <PackagingPOCreateItemsCard
                items={items}
                onItemsChange={setItems}
                showValidation={hasAttemptedSubmit}
                itemErrors={validation.itemErrors}
            />
        </div>
    );
}

CreatePackagingPOV2.displayName = 'CreatePackagingPOV2';
